import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { removeFromLocalStorage } from 'lib/localStorage';
import { isDarkModeForcedPage } from 'lib/theme';

import useLocalStorageState from './useLocalStorageState';

import LOCAL_STORAGE from 'constants/localStorage';

const OPTIONS = {
  light: 'light',
  dark: 'dark',
};

const useTheme = () => {
  const { pathname } = useRouter();
  const [isDarkModeActive, setDarkModeActive] = useLocalStorageState(
    LOCAL_STORAGE.IS_DARK_MODE_ACTIVE,
    false
  );

  const darkModeForcedPage = isDarkModeForcedPage(pathname);

  const currTheme =
    isDarkModeActive || darkModeForcedPage ? OPTIONS.dark : OPTIONS.light;

  const setTheme = useCallback(
    (newTheme) => {
      if (darkModeForcedPage) return;

      document.documentElement.classList = '';
      document.documentElement.classList.add(newTheme);
      setDarkModeActive(newTheme === OPTIONS.dark);
    },
    [darkModeForcedPage]
  );

  useEffect(() => {
    document.documentElement.classList = '';

    if (darkModeForcedPage) {
      document.documentElement.classList.add(OPTIONS.dark);
      return;
    }

    document.documentElement.classList.add(OPTIONS.light);
    setTheme(isDarkModeActive ? OPTIONS.dark : OPTIONS.light);
  }, [darkModeForcedPage, isDarkModeActive]);

  const resetUserThemePreference = useCallback(() => {
    removeFromLocalStorage(LOCAL_STORAGE.IS_DARK_MODE_ACTIVE);
    setTheme(false);
  }, [setTheme]);

  const changeThemeToStoredUserPreference = useCallback(() => {
    setTheme(isDarkModeActive ? OPTIONS.dark : OPTIONS.light);
  }, [isDarkModeActive]);

  return {
    options: OPTIONS,
    theme: currTheme,
    changeThemeToStoredUserPreference,
    resetUserThemePreference,
    setTheme,
    isDarkModeForcedPage: darkModeForcedPage,
  };
};

export default useTheme;
